import { SxProps } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { findClosestScrollableParent } from "csi/utility/htmlUtility";
import { FlexColumnProps } from "designSystem/components/flex/FlexColumn";
import { DashedOutlineWrapper, DndDropFunction, DndItemWithId, GlobalDropZone, useDndStore, useDropWithPosition } from "designSystem/dnd/index";
import { FlexColumn } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import dndCss from "../styles/dnd.module.css";

// String enum for mode options
export enum DndMode {
	Divider = "divider",
	Normal = "normal",
}

export interface ColumnDropWrapperProps<T extends DndItemWithId> extends PropsWithChildren, Partial<Omit<FlexColumnProps, "onDrop">> {
	/** don't outline the drop zone */
	disableDropZoneOutline?: boolean;
	/** disable drag while loading */
	disableDragWhileLoading?: boolean;
	/** types of items that can be accepted in the drop target */
	dropTypes: string[];
	/** force loading state */
	forceLoadingState?: boolean;
	/** true if this is the root drop wrapper */
	isRootDropWrapper?: boolean;
	/** mode of the wrapper, can be "divider" or "normal", divider is default */
	mode?: DndMode;
	/** called when a compatible item is dropped on the target */
	onDrop?: DndDropFunction<T>;
	/** called when a compatible item is hovered over the target */
	onHover?: DndDropFunction<T>;
	/** set internal container position to relative when no external container is provided */
	useExternalContainerPosition?: boolean;
}

export const ColumnDropWrapper = observer(function ColumnDropWrapper<T extends DndItemWithId>(props: ColumnDropWrapperProps<T>) {
	const {
		children,
		className,
		disableDragWhileLoading,
		disableDropZoneOutline,
		dropTypes,
		forceLoadingState,
		isRootDropWrapper,
		mode,
		onDrop,
		onHover,
		sx,
		useExternalContainerPosition,
		...otherProps
	} = props;

	const {
		establishScrollElement,
		isDragging,
		isFullScreen,
		isLoading,
		setIsOverTarget,
	} = useDndStore();
	const { drop, handlerId, isOver, isOverShallow } = useDropWithPosition(mode, dropTypes, onDrop, onHover);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isRootDropWrapper) {
			establishScrollElement(findClosestScrollableParent(ref.current));
		}
	}, []);

	useEffect(() => {
		setIsOverTarget(isOver || isOverShallow);
	}, [isOver, isOverShallow]);

	// if it's a local drop zone, attach drop zone to the ref that surrounds the children
	if (!isFullScreen) {
		drop(ref);
	}

	const enabledOutline = !disableDropZoneOutline && !isFullScreen && isDragging;

	const internalSx: SxProps = useExternalContainerPosition ? {
		...sx,
	} : {
		position: "relative",
		...sx,
	};

	return (
		<FlexColumn
			className={className}
			data-handler-id={handlerId}
			ref={ref}
			sx={{
				...internalSx,
			}}
			{...otherProps}
		>
			{(forceLoadingState || (disableDragWhileLoading && isLoading)) && (
				<div className={dndCss.dragLoadingIndicator}>
					<CircularProgress className={dndCss.circularProgressIndicator} />
				</div>
			)}
			<DashedOutlineWrapper
				active={isOver}
				enabled={enabledOutline}
			>
				{isFullScreen && isDragging && <GlobalDropZone drop={drop} handlerId={handlerId} />}
				{children}
			</DashedOutlineWrapper>
		</FlexColumn>
	);
});
